import { useEffect, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import { LoadingState } from "@/components/base/StateViews";
import { useAsync } from "@/hooks/useAsync";
import { usePager, type Page } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { getClasses } from "@/services/academicService";
import { getActiveSchoolYear, getCycles, getSchoolActiveYear, getSchoolInfo, getSchoolYears } from "@/services/schoolService";
import { getEnrollments, getGuardians, getStudentGuardians, getStudents } from "@/services/studentService";
import { getBalancesForYears, getReceipts } from "@/services/paymentService";
import { formatMoney, plural } from "@/utils/format";
import { useAuth } from "@/contexts/AuthContext";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import type { EnrollmentBalance, Student } from "@/types";
import { buildEnrollmentReceipt, toFeeSnapshot, type FeeSnapshot, type ReceiptData } from "@/utils/receipt";
import EnrollmentWizard from "./components/EnrollmentWizard";
import ReenrollPicker from "./components/ReenrollPicker";
import DossierModal from "./components/DossierModal";
import ReceiptPrintModal from "@/pages/dashboard/payments/components/ReceiptPrintModal";
import { cycleIdOf } from "@/utils/cycleScope";
import { ENROLLMENT_STATUS_LABEL, ENROLLMENT_STATUS_TONE } from "@/utils/enrollment";

async function load() {
  const [students, guardians, links, enrollments, classes, cycles, receipts, schoolYears, school] = await Promise.all([
    getStudents(),
    getGuardians(),
    getStudentGuardians(),
    getEnrollments(),
    getClasses(),
    getCycles(),
    getReceipts(),
    getSchoolYears(),
    getSchoolInfo(),
  ]);
  const balances = await getBalancesForYears(schoolYears.map((y) => y.label));
  return { students, guardians, links, enrollments, classes, cycles, balances, receipts, schoolYears, school };
}

/** Situation de frais d'une inscription (pour le reçu). */
function buildFees(enrollmentId: string, balances: EnrollmentBalance[]): FeeSnapshot[] {
  return (balances.find((b) => b.enrollmentId === enrollmentId)?.lines ?? []).map(toFeeSnapshot);
}

export default function EnrollmentsPage() {
  const activeYear = getActiveSchoolYear();
  const { allowedCycleIds, cycleScope, can } = useAuth();
  const isGlobal = cycleScope === "GLOBAL";
  const { data, loading, reload } = useAsync(load, []);
  const [yearFilter, setYearFilter] = useState(activeYear);
  const [cycleFilter, setCycleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const pagerPrimaire = usePager();
  const pagerSecondaire = usePager();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [presetStudent, setPresetStudent] = useState<Student | null>(null);
  const [dossier, setDossier] = useState<Student | null>(null);
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);
  const [pendingReceiptId, setPendingReceiptId] = useState<string | null>(null);

  // Ouvre automatiquement le reçu juste après une inscription réussie.
  useEffect(() => {
    if (!pendingReceiptId || !data) return;
    const enr = data.enrollments.find((e) => e.id === pendingReceiptId);
    const student = enr ? data.students.find((s) => s.id === enr.studentId) : null;
    setPendingReceiptId(null);
    if (!enr || !student) return;
    const cls = data.classes.find((c) => c.id === enr.classId);
    const cyc = data.cycles.find((c) => c.id === enr.cycleId);
    setReceiptData(
      buildEnrollmentReceipt({
        fees: buildFees(enr.id, data.balances),
        receipts: data.receipts.filter((v) => v.enrollmentId === enr.id),
        student: {
          name: `${student.firstName} ${student.lastName}`,
          matricule: student.matricule,
          className: cls?.name ?? "—",
          cycleLabel: cyc?.label ?? "—",
          schoolYear: enr.schoolYear,
        },
        title: "Reçu d'inscription",
      }),
    );
  }, [pendingReceiptId, data]);

  if (loading && !data) return <LoadingState label="Chargement des inscriptions..." />;
  if (!data) return <LoadingState label="Chargement des inscriptions..." />;
  const { students, guardians, links, enrollments, classes, cycles, balances, receipts, schoolYears, school } = data;
  const primaireId = cycleIdOf(cycles, "PRIMAIRE");
  const secondaireId = cycleIdOf(cycles, "SECONDAIRE");

  const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
  const scopedEnrollments = enrollments.filter((e) => allowedCycleIds.includes(e.cycleId));
  const scopedClasses = classes.filter((c) => allowedCycleIds.includes(c.cycleId));
  const scopedStudents = isGlobal
    ? students
    : students.filter((s) => scopedEnrollments.some((e) => e.studentId === s.id));

  const studentOf = (id: string) => scopedStudents.find((s) => s.id === id);
  const className = (id: string) => scopedClasses.find((c) => c.id === id)?.name ?? "—";
  const primaryGuardian = (studentId: string) => {
    const link = links.find((l) => l.studentId === studentId && l.isPrimary) ?? links.find((l) => l.studentId === studentId);
    if (!link) return null;
    return guardians.find((g) => g.id === link.guardianId) ?? null;
  };
  const feesFor = (enrollmentId: string) => {
    const balance = balances.find((b) => b.enrollmentId === enrollmentId);
    return { due: balance?.totalDue ?? 0, paid: balance?.totalPaid ?? 0, rest: balance?.remaining ?? 0 };
  };

  const openReceipt = (enrollmentId: string) => {
    const enr = enrollments.find((e) => e.id === enrollmentId);
    const student = enr ? students.find((s) => s.id === enr.studentId) : null;
    if (!enr || !student) return;
    const cls = classes.find((c) => c.id === enr.classId);
    const cyc = cycles.find((c) => c.id === enr.cycleId);
    setReceiptData(
      buildEnrollmentReceipt({
        fees: buildFees(enr.id, balances),
        receipts: receipts.filter((v) => v.enrollmentId === enr.id),
        student: {
          name: `${student.firstName} ${student.lastName}`,
          matricule: student.matricule,
          className: cls?.name ?? "—",
          cycleLabel: cyc?.label ?? "—",
          schoolYear: enr.schoolYear,
        },
        title: "Reçu d'inscription",
      }),
    );
  };

  // Élèves déjà inscrits ou réinscrits pour l'année active : on ne peut pas les réinscrire une seconde fois.
  const currentYear = getSchoolActiveYear();
  const enrolledThisYear = new Set(
    enrollments.filter((e) => e.schoolYear === currentYear && e.status !== "CANCELLED").map((e) => e.studentId),
  );

  const rows = scopedEnrollments
    .filter((e) => yearFilter === "all" || e.schoolYear === yearFilter)
    .filter((e) => cycleFilter === "all" || e.cycleId === cycleFilter)
    .filter((e) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      const s = studentOf(e.studentId);
      if (!s) return false;
      return s.lastName.toLowerCase().includes(q) || s.firstName.toLowerCase().includes(q) || s.matricule.toLowerCase().includes(q);
    })
    .sort((a, b) => b.schoolYear.localeCompare(a.schoolYear));

  const openReenroll = (student: Student) => {
    setPresetStudent(student);
    setPickerOpen(false);
    setWizardOpen(true);
  };

  const renderTable = (page: Page<(typeof rows)[number]>) => (
    <>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[920px] text-sm">
        <thead>
          <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
            <th className="px-4 py-3 font-medium">Matricule</th>
            <th className="px-4 py-3 font-medium">Élève</th>
            <th className="px-4 py-3 font-medium">Classe</th>
            <th className="px-4 py-3 font-medium">Responsable</th>
            <th className="px-4 py-3 font-medium">Frais payé / restant</th>
            <th className="px-4 py-3 font-medium">Statut</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {page.items.map((e) => {
            const s = studentOf(e.studentId);
            const g = primaryGuardian(e.studentId);
            const fees = feesFor(e.id);
            if (!s) return null;
            return (
              <tr key={e.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{s.matricule}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar lastName={s.lastName} firstName={s.firstName} src={s.photo || undefined} size="sm" />
                    <div>
                      <p className="font-medium text-foreground-900">
                        {s.firstName} {s.lastName}
                      </p>
                      <p className="text-xs text-foreground-500">{e.schoolYear}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-foreground-800">{className(e.classId)}</td>
                <td className="px-4 py-3">
                  {g ? (
                    <div>
                      <p className="text-foreground-800">
                        {g.firstName} {g.lastName}
                      </p>
                      <p className="text-xs text-foreground-500">{g.phone}</p>
                    </div>
                  ) : (
                    <span className="text-foreground-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs">
                    <span className="font-medium text-primary-700">{formatMoney(fees.paid)}</span>
                    <span className="text-foreground-400"> / </span>
                    <span className={fees.rest > 0 ? "font-medium text-accent-700" : "text-foreground-500"}>
                      {formatMoney(fees.rest)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={ENROLLMENT_STATUS_TONE[e.status]}>{ENROLLMENT_STATUS_LABEL[e.status]}</Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openReceipt(e.id)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-receipt-line"></i>
                      Reçu
                    </button>
                    <button
                      onClick={() => setDossier(s)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-eye-line"></i>
                      Dossier
                    </button>
                    {!enrolledThisYear.has(s.id) && (
                    <button
                      onClick={() => openReenroll(s)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs text-primary-800 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line"></i>
                      Réinscrire
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {page.total === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-sm text-foreground-400">
                Aucune inscription pour ces filtres.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <Pagination pager={page} className="border-t border-background-200" />
    </>
  );

  const primaire = rows.filter((e) => e.cycleId === primaireId);
  const secondaire = rows.filter((e) => e.cycleId === secondaireId);
  const filterKey = `${yearFilter}|${cycleFilter}|${search}`;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Inscriptions & Réinscriptions"
        subtitle="Un élève = un dossier permanent. Une réinscription crée toujours une nouvelle inscription."
        icon="ri-file-add-line"
        actions={
          <>
            {can("enrollments:reenroll") && (
              <Button variant="outline" icon="ri-user-follow-line" onClick={() => setPickerOpen(true)}>
                Réinscrire un élève
              </Button>
            )}
            {can("enrollments:enroll") && (
              <Button
                icon="ri-user-add-line"
                onClick={() => {
                  setPresetStudent(null);
                  setWizardOpen(true);
                }}
              >
                Nouvelle inscription
              </Button>
            )}
          </>
        }
      />

      <CycleScopeBanner className="mb-5" />

      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
        <Badge tone="primary" icon="ri-calendar-check-line">
          Année scolaire active : {activeYear}
        </Badge>
        <span className="text-xs text-foreground-500">
          Les paiements sont rattachés à leur année scolaire et ne sont jamais écrasés.
        </span>
      </div>

      <div className="mb-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
          >
            <option value="all">Toutes les années</option>
            {schoolYears.map((y) => (
              <option key={y.id} value={y.label}>
                {y.label}
                {y.active ? " (active)" : ""}
              </option>
            ))}
          </select>
          <select
            value={cycleFilter}
            onChange={(e) => setCycleFilter(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
          >
            <option value="all">Tous les cycles</option>
            {visibleCycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
          <i className="ri-search-line text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, prénom ou matricule..."
            className="w-full sm:w-56 bg-transparent text-sm focus:outline-none"
          />
        </div>
      </div>

      <div className="space-y-6">
        {allowedCycleIds.includes(primaireId) &&
          (cycleFilter === "all" || cycleFilter === primaireId) && (
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-warning-50 text-warning-700 flex items-center justify-center">
                  <i className="ri-seedling-line"></i>
                </span>
                <h3 className="text-sm font-semibold font-heading text-foreground-950">Cycle Primaire</h3>
              </div>
              <Badge tone="warning">{plural(primaire.length, "inscription")}</Badge>
            </div>
            {renderTable(pagerPrimaire.slice(primaire, filterKey))}
          </Card>
        )}

        {allowedCycleIds.includes(secondaireId) &&
          (cycleFilter === "all" || cycleFilter === secondaireId) && (
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className="ri-book-read-line"></i>
                </span>
                <h3 className="text-sm font-semibold font-heading text-foreground-950">Cycle Secondaire</h3>
              </div>
              <Badge tone="secondary">{plural(secondaire.length, "inscription")}</Badge>
            </div>
            {renderTable(pagerSecondaire.slice(secondaire, filterKey))}
          </Card>
        )}
      </div>

      <EnrollmentWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={reload}
        onEnrolled={(id) => setPendingReceiptId(id)}
        onProvisional={setReceiptData}
        cycles={visibleCycles}
        classes={scopedClasses}
        schoolYears={schoolYears}
        activeYear={activeYear}
        presetStudent={presetStudent}
        history={
          presetStudent
            ? enrollments.filter((e) => e.studentId === presetStudent.id && e.status !== "CANCELLED")
            : []
        }
        allClasses={classes}
      />

      <ReenrollPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        students={scopedStudents}
        enrollments={scopedEnrollments}
        classes={scopedClasses}
        currentYear={currentYear}
        onSelect={openReenroll}
      />

      <DossierModal
        open={dossier !== null}
        onClose={() => setDossier(null)}
        student={dossier}
        classes={scopedClasses}
        activeYear={activeYear}
      />

      <ReceiptPrintModal
        open={receiptData !== null}
        onClose={() => setReceiptData(null)}
        data={receiptData}
        school={school}
      />
    </div>
  );
}